/**
 * Home page.
 *
 * The landing page is where a first-time visitor has to understand that this is a
 * group location-decision tool rather than an event sign-up form, so the page runs
 * as one argument: the problem in the group chat, the four steps, a sample
 * recommendation result, what each capability answers, and what happens to a
 * precise location. It is assembled from the presentation modules under
 * `components/landing`, each of which owns one section.
 *
 * The page itself only knows two things beyond that: whether a session exists
 * (which decides the main call to action and whether the promotion link is
 * useful) and the feedback card that has to stay reachable. It never reads event
 * data, and it never renders real recommendations.
 */

import { useTranslation } from 'react-i18next'

import { LandingCta } from '../components/landing/landing-cta'
import { LandingFeatures } from '../components/landing/landing-features'
import { LandingHero } from '../components/landing/landing-hero'
import { LandingProblem } from '../components/landing/landing-problem'
import { LANDING_STEPS_ID, LandingSteps } from '../components/landing/landing-steps'
import { PrivacyCallout } from '../components/landing/privacy-callout'
import { RecommendationPreview } from '../components/landing/recommendation-preview'
import { PromotionLinkCard } from '../components/referral/promotion-link'
import { ContactCard } from '../components/shared/contact-card'
import { useCurrentUser } from '../hooks/use-auth'

/** Id of the in-page how-it-works section the secondary action scrolls to. */
const HOW_IT_WORKS_TARGET = LANDING_STEPS_ID

export function HomePage() {
  const { t } = useTranslation()
  const { isAuthenticated } = useCurrentUser()

  // Signing in is the only way in, and an existing session goes straight to the
  // account area rather than back to the sign-in form.
  const primaryTo = isAuthenticated ? '/me' : '/login/email'
  const primaryLabel = isAuthenticated ? t('home.finalCtaSignedIn') : t('home.ctaPrimary')

  return (
    <div className="flex flex-col">
      <LandingHero
        primaryTo={primaryTo}
        primaryLabel={primaryLabel}
        secondaryTarget={HOW_IT_WORKS_TARGET}
        secondaryLabel={t('home.ctaSecondary')}
      />

      <LandingProblem />
      <LandingSteps />
      <RecommendationPreview />
      <LandingFeatures />
      <PrivacyCallout />

      <LandingCta primaryTo={primaryTo} primaryLabel={primaryLabel} />

      {/* Feedback stays reachable from the landing page itself: it is the only
          bug channel for a visitor who has not signed in. */}
      <section className="landing-section pt-0" aria-label={t('home.contactTitle')}>
        <div className="landing-container flex flex-col gap-6">
          <ContactCard />
          {/* The home page is the destination of a site promotion link, so it is
              where a signed-in user picks their own copy up. */}
          {isAuthenticated ? <PromotionLinkCard /> : null}
        </div>
      </section>
    </div>
  )
}
